import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider,
  TextField,
  Collapse,
  Tabs,
  Tab,
  Link,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { audiencesApi, type AudienceScore } from '../../services/audiences';
import { retailerCatalogApi } from '../../services/retailerCatalog';
import type { Audience, AudienceSegment, Catalogue, SegmentPrompt, MeasurementRun, Cadence } from '@traaker/shared';
import { resolveMeasureCadence, describeCadence, isCadenceConfigurable } from '@traaker/shared';
import { SegmentEditorDialog } from './SegmentEditorDialog';
import { MeasurementResults, RunProgress } from './MeasurementResults';
import { MeasurementExplorer } from './MeasurementExplorer';
import { MeasurementSynthesis } from './MeasurementSynthesis';
import { MeasurementHistory } from './MeasurementHistory';
import { CatalogueIcon } from './CatalogueIcon';
import { EngineLogo } from '../../components/brand/EngineLogo';
import { Flag, ZoneFlag } from '../dashboard/Flag';
import { languageFlag, resolveZone } from '../dashboard/geoFlags';

/** Same red/orange/green bands as the optimization page's score chip. */
function scoreBand(score: number): 'success' | 'warning' | 'error' {
  return score >= 70 ? 'success' : score >= 40 ? 'warning' : 'error';
}
function scoreColor(score: number): string {
  const band = scoreBand(score);
  return band === 'success' ? '#2e7d32' : band === 'warning' ? '#ed6c02' : '#d32f2f';
}

/** Relative date for the card's last-run stamp ("il y a 3 j"). */
function relativeDate(iso?: string): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} j`;
  const w = Math.floor(days / 7);
  if (w < 5) return `il y a ${w} sem.`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

/** Circular score gauge (SVG, 44px) — color-graded ring with the score centered. */
function ScoreGauge({ score, size = 44 }: { score: number; size?: number }) {
  const stroke = 4;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(1, score / 100)) * circ;
  const color = scoreColor(score);
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeOpacity={0.12} strokeWidth={stroke} />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circ - filled}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 13, color }}>
          {score}
        </Typography>
      </Box>
    </Box>
  );
}

/** Dashed placeholder ring for the empty state (no run yet). */
function ScoreGaugePlaceholder({ size = 44 }: { size?: number }) {
  const stroke = 4;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, color: 'text.disabled' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.3}
          strokeWidth={stroke}
          strokeDasharray="4 3"
        />
      </svg>
    </Box>
  );
}

/** Inline score-history sparkline (60x20) — no chart lib, ≥2 points required. */
function Sparkline({ history }: { history?: number[] }) {
  if (!history || history.length < 2) return null;
  const w = 60;
  const h = 20;
  const pad = 2;
  const min = Math.min(...history);
  const max = Math.max(...history);
  const span = max - min || 1;
  const points = history.map((v, i) => {
    const x = pad + (i / (history.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return `${x},${y}`;
  });
  const last = history[history.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={scoreColor(last)}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Slim stacked coverage bar (green/amber/blue/grey) with a tallies tooltip. */
function CoverageMiniBar({ coverage }: { coverage: { covered: number; leaked: number; mentioned: number; none: number } }) {
  const total = coverage.covered + coverage.leaked + coverage.mentioned + coverage.none || 1;
  const seg = (n: number, color: string) =>
    n > 0 ? <Box sx={{ width: `${(n / total) * 100}%`, bgcolor: color, height: 6 }} /> : null;
  return (
    <Tooltip
      title={`${coverage.covered} recommandé · ${coverage.leaked} fuite · ${coverage.mentioned} mentionné · ${coverage.none} absent`}
    >
      <Box sx={{ display: 'flex', width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
        {seg(coverage.covered, '#2e7d32')}
        {seg(coverage.leaked, '#ed6c02')}
        {seg(coverage.mentioned, '#0288d1')}
        {seg(coverage.none, '#bdbdbd')}
      </Box>
    </Tooltip>
  );
}

/**
 * GEO Measurement ("Visibilité GEO"). A "Mesure" (Audience in code) groups client
 * Segments; each segment generates its own shopper prompts. Detail is 3 tabs:
 * Synthèse (insights) · Segments (config) · Réponses IA (per-answer explorer).
 */
export default function ProductMeasurement() {
  const { activeBrand } = useBrand();
  const { show } = useToast();
  const brandId = activeBrand?.brandId;

  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [scores, setScores] = useState<Record<string, AudienceScore>>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Audience | null>(null);

  const reload = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const [auds, cats] = await Promise.all([
        audiencesApi.list(brandId),
        retailerCatalogApi.list(brandId),
      ]);
      setAudiences(auds);
      setCatalogues(cats);
      // Scores are best-effort — a failure must not blank the list.
      audiencesApi
        .scores(brandId)
        .then((rows) => setScores(Object.fromEntries(rows.map((r) => [r.audienceId, r]))))
        .catch(() => setScores({}));
    } catch (e: any) {
      show(e?.message || 'Erreur de chargement', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [brandId, show]);

  useEffect(() => {
    reload();
  }, [reload]);

  const deleteAudience = async (a: Audience, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!brandId) return;
    if (!confirm(`Supprimer la mesure « ${a.name} » et tous ses segments ?`)) return;
    try {
      await audiencesApi.remove(brandId, a.audienceId);
      show('Mesure supprimée', { severity: 'success' });
      reload();
    } catch (err: any) {
      show(err?.message || 'Suppression impossible', { severity: 'error' });
    }
  };

  if (!brandId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Sélectionnez une marque.</Alert>
      </Box>
    );
  }

  if (selected) {
    return (
      <AudienceDetail
        brandId={brandId}
        audience={selected}
        catalogues={catalogues}
        onBack={() => {
          setSelected(null);
          reload();
        }}
      />
    );
  }

  const catFor = (id: string) => catalogues.find((c) => c.catalogueId === id);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5">Visibilité GEO</Typography>
        <Box sx={{ flex: 1 }} />
        <CreateAudienceButton
          brandId={brandId}
          disabled={catalogues.length === 0}
          onCreated={(a) => {
            reload();
            setSelected(a);
          }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mesurez si les moteurs IA recommandent vos produits pour différents profils d'acheteurs.
        Une <strong>mesure</strong> regroupe plusieurs <strong>segments</strong> clients ; chaque
        segment génère ses propres prompts.
      </Typography>

      {catalogues.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Créez d'abord un catalogue dans l'onglet Catalogue.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : audiences.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
          <InsightsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            Aucune mesure. Créez-en une pour configurer vos segments clients.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mesure</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell>Dernière analyse</TableCell>
                <TableCell align="center">Score GEO</TableCell>
                <TableCell align="center">Évolution</TableCell>
                <TableCell align="center">vs précédent</TableCell>
                <TableCell align="center">Couverture</TableCell>
                <TableCell align="center">Prompts</TableCell>
                <TableCell>Segments</TableCell>
                <TableCell>Catalogues</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audiences.map((a) => {
                const cats = [...new Set(a.segments.map((s) => s.catalogueId))]
                  .map(catFor)
                  .filter(Boolean) as Catalogue[];
                const sc = scores[a.audienceId];
                const hasRun = !!sc;
                const promptTotal = a.segments.reduce((sum, s) => sum + (s.promptCount || 0), 0);
                const statusChip = a.lastRunStatus ? (
                  <Chip
                    size="small"
                    label={
                      a.lastRunStatus === 'running'
                        ? 'En cours'
                        : a.lastRunStatus === 'partial'
                          ? 'Partiel'
                          : a.lastRunStatus === 'completed'
                            ? 'Terminé'
                            : a.lastRunStatus
                    }
                    color={
                      a.lastRunStatus === 'running'
                        ? 'info'
                        : a.lastRunStatus === 'partial'
                          ? 'warning'
                          : 'default'
                    }
                  />
                ) : (
                  <Typography variant="body2" color="text.disabled">—</Typography>
                );
                return (
                  <TableRow
                    key={a.audienceId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelected(a)}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <InsightsIcon color="primary" fontSize="small" />
                        <Typography fontWeight={600} noWrap>
                          {a.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{statusChip}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={hasRun ? 'text.primary' : 'text.disabled'}>
                        {hasRun ? relativeDate(a.lastRunAt) : 'Aucune analyse'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {hasRun ? (
                        <Tooltip title="Score GEO (moyenne des segments)">
                          <Box sx={{ display: 'inline-flex' }}>
                            <ScoreGauge score={sc.score} size={40} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: 'inline-flex' }}>
                          <ScoreGaugePlaceholder size={40} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {hasRun ? (
                        <Box sx={{ display: 'inline-flex' }}>
                          <Sparkline history={sc.history} />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {hasRun && sc.prevScore != null && sc.score !== sc.prevScore ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          spacing={0.15}
                          sx={{ color: sc.score > sc.prevScore ? 'success.main' : 'error.main' }}
                        >
                          {sc.score > sc.prevScore ? (
                            <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                          )}
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {Math.abs(sc.score - sc.prevScore)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 90 }}>
                      {hasRun && a.lastRunCoverage ? (
                        <CoverageMiniBar coverage={a.lastRunCoverage} />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{promptTotal || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {a.segments.length > 0 && (
                        <Stack direction="row" alignItems="center">
                          {a.segments.slice(0, 6).map((s, i) => (
                            <Box
                              key={s.segmentId}
                              title={s.name}
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                ml: i === 0 ? 0 : '-6px',
                              }}
                            >
                              {s.avatar || '🧑'}
                            </Box>
                          ))}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                            {a.segments.length > 6 && `+${a.segments.length - 6} · `}
                            {a.segments.length} segment{a.segments.length !== 1 ? 's' : ''}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                        {cats.slice(0, 2).map((c) => (
                          <Chip
                            key={c.catalogueId}
                            size="small"
                            variant="outlined"
                            icon={<CatalogueIcon icon={c.icon} sx={{ fontSize: 15 }} />}
                            label={c.name}
                          />
                        ))}
                        {cats.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{cats.length - 2}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={(e) => deleteAudience(a, e)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

const MEASURE_CADENCE_OPTIONS: Array<{ value: Cadence; label: string }> = [
  { value: 'off', label: 'Désactivée' },
  { value: { interval: 1, unit: 'days' }, label: 'Quotidienne' },
  { value: { interval: 1, unit: 'weeks' }, label: 'Hebdomadaire' },
  { value: { interval: 1, unit: 'months' }, label: 'Mensuelle' },
];

function cadenceKey(c: Cadence): string {
  return c === 'off' ? 'off' : `${c.interval}-${c.unit}`;
}

/**
 * "Fréquence" control for the measurement cadence (docs/features/
 * retailer-catalog.md §6.2). Self-serve tiers
 * are locked to the resolved default (same `isCadenceConfigurable` rule as
 * Brand Perception's CadenceEditor) — a read-only chip; agency/enterprise get
 * a select with a rough credit-cost hint per fire.
 */
function MeasureCadenceControl({
  tier,
  audience,
  onChange,
}: {
  tier: string;
  audience: Audience;
  onChange: (cadence: Cadence) => void;
}) {
  const effective = resolveMeasureCadence(tier, audience);

  if (!isCadenceConfigurable(tier)) {
    return (
      <Tooltip title="Fréquence de mesure (incluse dans votre offre)">
        <Chip size="small" variant="outlined" label={describeCadence(effective)} />
      </Tooltip>
    );
  }

  const callsPerFire = audience.segments.reduce(
    (sum, s) => sum + s.promptCount * s.engines.length,
    0,
  );
  const firesPerMonth = (c: Cadence): number => {
    if (c === 'off') return 0;
    const days = c.unit === 'days' ? c.interval : c.unit === 'weeks' ? c.interval * 7 : c.interval * 30;
    return Math.round(30 / days);
  };
  const monthlyCredits = callsPerFire * firesPerMonth(effective);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Fréquence
      </Typography>
      <FormControl size="small">
        <Select
          value={cadenceKey(effective)}
          onChange={(e) => {
            const opt = MEASURE_CADENCE_OPTIONS.find((o) => cadenceKey(o.value) === e.target.value);
            if (opt) onChange(opt.value);
          }}
          sx={{ minWidth: 150 }}
        >
          {MEASURE_CADENCE_OPTIONS.map((o) => (
            <MenuItem key={cadenceKey(o.value)} value={cadenceKey(o.value)}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {effective !== 'off' && callsPerFire > 0 && (
        <Tooltip title="prompts × moteurs × déclenchements/mois">
          <Typography variant="caption" color="text.secondary">
            ≈ {monthlyCredits} crédits/mois
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
}

function CreateAudienceButton({
  brandId,
  disabled,
  onCreated,
}: {
  brandId: string;
  disabled: boolean;
  onCreated: (a: Audience) => void;
}) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const a = await audiencesApi.create(brandId, name.trim());
      setName('');
      setOpen(false);
      onCreated(a);
    } catch (e: any) {
      show(e?.message || 'Création impossible', { severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        data-tour="product-measure-new"
        variant="contained"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Nouvelle mesure
      </Button>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        autoFocus
        placeholder="Nom de la mesure"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && create()}
      />
      <Button variant="contained" onClick={create} disabled={busy || !name.trim()}>
        {busy ? <CircularProgress size={18} /> : 'Créer'}
      </Button>
      <Button onClick={() => setOpen(false)}>Annuler</Button>
    </Stack>
  );
}

// ------------------------------------------------------------------
// Mesure detail — 4 tabs: Synthèse / Segments / Réponses IA / Historique
// ------------------------------------------------------------------
type DetailTab = 'summary' | 'segments' | 'responses' | 'history';

function AudienceDetail({
  brandId,
  audience,
  catalogues,
  onBack,
}: {
  brandId: string;
  audience: Audience;
  catalogues: Catalogue[];
  onBack: () => void;
}) {
  const { show } = useToast();
  const { tenant } = useAuth();
  const tier = tenant?.tier || 'basic';
  const [aud, setAud] = useState<Audience>(audience);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<AudienceSegment | null>(null);
  const [run, setRun] = useState<MeasurementRun | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [tab, setTab] = useState<DetailTab>('segments');
  const [promptsRefreshToken, setPromptsRefreshToken] = useState(0);
  const [trend, setTrend] = useState<{ score: number; prevScore?: number } | null>(null);

  const cat = (id: string) => catalogues.find((c) => c.catalogueId === id);
  // The selected run for Synthèse/Réponses IA (defaults to the latest — set
  // whenever a fresh run lands, unless the user picked an older one via the
  // Historique tab). Never the latest run while it's still `running` — same
  // gate as before (a picked historical run is always terminal, see the
  // Historique table's "Voir" disable).
  const displayRunId = selectedRunId || (run && run.status !== 'running' ? run.runId : undefined);

  // Score + trend for the detail-page header (item e) — same endpoint the
  // list cards use; backfilled lazily server-side for pre-2026-07-21 runs.
  useEffect(() => {
    audiencesApi
      .scores(brandId)
      .then((rows) => setTrend(rows.find((r) => r.audienceId === aud.audienceId) || null))
      .catch(() => setTrend(null));
  }, [brandId, aud.audienceId, run?.status]);

  // Load the latest run on mount; default to the Synthèse tab when results exist.
  useEffect(() => {
    audiencesApi
      .runProgress(brandId, aud.audienceId)
      .then((r) => {
        setRun(r);
        if (r && (r.status === 'completed' || r.status === 'partial')) setTab('summary');
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while running.
  useEffect(() => {
    if (run?.status !== 'running') return;
    const h = setInterval(async () => {
      const r = await audiencesApi.runProgress(brandId, aud.audienceId, run.runId);
      setRun(r);
      if (r && r.status !== 'running') setTab('summary');
    }, 4000);
    return () => clearInterval(h);
  }, [brandId, aud.audienceId, run?.status, run?.runId]);

  const startRun = async () => {
    setStarting(true);
    try {
      const r = await audiencesApi.startRun(brandId, aud.audienceId);
      setRun(r);
      setSelectedRunId(null); // follow the new run, not a stale Historique pick
      show('Analyse lancée', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Lancement impossible', { severity: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const saveSegments = async (segments: AudienceSegment[]): Promise<Audience | undefined> => {
    try {
      const updated = await audiencesApi.update(brandId, aud.audienceId, { segments });
      setAud(updated);
      return updated;
    } catch (e: any) {
      show(e?.message || 'Enregistrement impossible', { severity: 'error' });
      return undefined;
    }
  };

  const onSaveSegment = async (s: AudienceSegment, importCsv?: string) => {
    const existingIds = new Set(aud.segments.map((x) => x.segmentId));
    const exists = s.segmentId && existingIds.has(s.segmentId);
    const next = exists
      ? aud.segments.map((x) => (x.segmentId === s.segmentId ? s : x))
      : [...aud.segments, s];
    setEditorOpen(false);
    setEditingSegment(null);
    const updated = await saveSegments(next);
    if (!importCsv || !updated) return;
    // New segment → the API generated its segmentId; find it as the one not
    // present before the save.
    const savedSegmentId = exists ? s.segmentId : updated.segments.find((x) => !existingIds.has(x.segmentId))?.segmentId;
    if (!savedSegmentId) return;
    try {
      const result = await audiencesApi.importPrompts(brandId, updated.audienceId, savedSegmentId, importCsv);
      const { tooShort, tooLong, duplicates } = result.dropped;
      const droppedTotal = tooShort + tooLong + duplicates;
      show(
        droppedTotal
          ? `${result.imported} prompts importés, ${droppedTotal} ignorés (${tooShort} trop courts, ${tooLong} trop longs, ${duplicates} doublons)`
          : `${result.imported} prompts importés`,
        { severity: droppedTotal ? 'warning' : 'success' },
      );
      setPromptsRefreshToken((t) => t + 1);
    } catch (e: any) {
      show(e?.message || 'Import impossible', { severity: 'error' });
    }
  };

  const deleteSegment = (segmentId: string) => {
    if (!confirm('Supprimer ce segment ?')) return;
    saveSegments(aud.segments.filter((x) => x.segmentId !== segmentId));
  };

  const segmentsPanel = (
    <>
      <Stack spacing={2}>
        {aud.segments.map((s) => (
          <SegmentCard
            key={s.segmentId}
            brandId={brandId}
            audienceId={aud.audienceId}
            segment={s}
            catalogue={cat(s.catalogueId)}
            onEdit={() => {
              setEditingSegment(s);
              setEditorOpen(true);
            }}
            onDelete={() => deleteSegment(s.segmentId)}
            refreshToken={promptsRefreshToken}
          />
        ))}
      </Stack>
      <Button
        startIcon={<AddIcon />}
        sx={{ mt: 2 }}
        onClick={() => {
          setEditingSegment(null);
          setEditorOpen(true);
        }}
        disabled={catalogues.length === 0}
      >
        Ajouter un segment
      </Button>
    </>
  );

  const setCadence = async (cadence: Cadence) => {
    try {
      const updated = await audiencesApi.update(brandId, aud.audienceId, { measureCadence: cadence });
      setAud(updated);
      show('Fréquence mise à jour', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Mise à jour impossible', { severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
          Retour
        </Button>
        <Box sx={{ flex: 1 }} />
        <MeasureCadenceControl tier={tier} audience={aud} onChange={setCadence} />
        <Button
          variant="contained"
          startIcon={starting ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          onClick={startRun}
          disabled={starting || run?.status === 'running' || aud.segments.length === 0}
        >
          Lancer l'analyse
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Typography variant="h5">{aud.name}</Typography>
        {trend && (
          <Tooltip title="Score GEO (moyenne des segments)">
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="body2" fontWeight={700}>
                {trend.score}/100
              </Typography>
              {trend.prevScore != null && trend.score !== trend.prevScore && (
                <Typography
                  variant="caption"
                  sx={{ color: trend.score > trend.prevScore ? 'success.main' : 'error.main' }}
                >
                  {trend.score > trend.prevScore ? '↑' : '↓'} {Math.abs(trend.score - trend.prevScore)}
                </Typography>
              )}
            </Stack>
          </Tooltip>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {aud.segments.length} segment{aud.segments.length !== 1 ? 's' : ''} · les prompts de tous
        les segments sont envoyés aux moteurs IA lors de l'analyse.
      </Typography>

      {run?.status === 'running' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <RunProgress completed={run.completed} total={run.total} />
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Synthèse" value="summary" disabled={!displayRunId} />
        <Tab label="Segments" value="segments" />
        <Tab label="Réponses IA" value="responses" disabled={!displayRunId} />
        <Tab label="Historique" value="history" />
      </Tabs>

      {tab === 'segments' && segmentsPanel}
      {tab === 'summary' &&
        (displayRunId ? (
          <Stack spacing={3}>
            <MeasurementSynthesis brandId={brandId} audience={aud} runId={displayRunId} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Couverture par segment
              </Typography>
              <MeasurementResults brandId={brandId} audienceId={aud.audienceId} runId={displayRunId} />
            </Box>
          </Stack>
        ) : (
          <Alert severity="info">Lancez une analyse pour voir la synthèse.</Alert>
        ))}
      {tab === 'responses' &&
        (displayRunId ? (
          <MeasurementExplorer brandId={brandId} audience={aud} runId={displayRunId} />
        ) : (
          <Alert severity="info">Lancez une analyse pour explorer les réponses IA.</Alert>
        ))}
      {tab === 'history' && (
        <MeasurementHistory
          brandId={brandId}
          audienceId={aud.audienceId}
          activeRunId={displayRunId}
          onSelectRun={(runId) => {
            setSelectedRunId(runId);
            setTab('summary');
          }}
        />
      )}

      {editorOpen && (
        <SegmentEditorDialog
          open
          segment={editingSegment}
          catalogues={catalogues}
          onClose={() => {
            setEditorOpen(false);
            setEditingSegment(null);
          }}
          onSave={onSaveSegment}
        />
      )}
    </Box>
  );
}

// ------------------------------------------------------------------
// Segment card — squared, catalog icon, prompts collapse
// ------------------------------------------------------------------
function SegmentCard({
  brandId,
  audienceId,
  segment,
  catalogue,
  onEdit,
  onDelete,
  refreshToken,
}: {
  brandId: string;
  audienceId: string;
  segment: AudienceSegment;
  catalogue?: Catalogue;
  onEdit: () => void;
  onDelete: () => void;
  /** Bumped by the parent after a CSV import so this card's prompt list reloads. */
  refreshToken?: number;
}) {
  const { show } = useToast();
  const [prompts, setPrompts] = useState<SegmentPrompt[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadPrompts = useCallback(async () => {
    try {
      setPrompts(await audiencesApi.listPrompts(brandId, audienceId, segment.segmentId));
    } catch {
      /* ignore */
    }
  }, [brandId, audienceId, segment.segmentId]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts, refreshToken]);

  const generate = async () => {
    setGenerating(true);
    try {
      const rows = await audiencesApi.generatePrompts(brandId, audienceId, segment.segmentId);
      setPrompts(rows);
      setExpanded(true);
      show(`${rows.length} prompts générés`, { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Génération impossible', { severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const toggleActive = async (p: SegmentPrompt) => {
    await audiencesApi.updatePrompt(brandId, audienceId, segment.segmentId, p.promptId, {
      active: !p.active,
    });
    setPrompts((prev) => prev?.map((x) => (x.promptId === p.promptId ? { ...x, active: !x.active } : x)) || null);
  };

  const startEdit = (p: SegmentPrompt) => {
    setEditingId(p.promptId);
    setEditText(p.text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };
  const saveEdit = async (p: SegmentPrompt) => {
    const text = editText.trim();
    if (!text || text === p.text) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      await audiencesApi.updatePrompt(brandId, audienceId, segment.segmentId, p.promptId, { text });
      cancelEdit();
      await loadPrompts(); // refresh text + recomputed closest SKU
      show('Prompt mis à jour', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Modification impossible', { severity: 'error' });
    } finally {
      setSavingEdit(false);
    }
  };

  const activeCount = prompts?.filter((p) => p.active).length ?? 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          {/* Persona face — personifies the segment; catalog icon stays a chip below. */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              flexShrink: 0,
            }}
          >
            {segment.avatar || '🧑'}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={600}>{segment.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {segment.description}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                icon={<CatalogueIcon icon={catalogue?.icon} sx={{ fontSize: 15 }} />}
                label={catalogue?.name || segment.catalogueId}
              />
              {segment.languages.map((l) => (
                <Flag key={l} countryCode={languageFlag(l)} size={16} title={l.toUpperCase()} />
              ))}
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 0.5 }}>
                {segment.engines.map((e) => (
                  <span key={e} title={e}><EngineLogo engine={e} size={16} /></span>
                ))}
              </Stack>
              {(segment.zones || []).map((z) => (
                <Chip
                  key={z}
                  size="small"
                  variant="outlined"
                  icon={<ZoneFlag zone={resolveZone(z)} size={14} />}
                  label={z}
                />
              ))}
            </Stack>
          </Box>
          <IconButton size="small" onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ px: 2 }}>
        <Button
          size="small"
          startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
          onClick={generate}
          disabled={generating}
        >
          {prompts && prompts.length ? 'Régénérer les prompts' : 'Générer les prompts'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {prompts && prompts.length > 0 && (
          <Button
            size="small"
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setExpanded((e) => !e)}
          >
            {activeCount}/{prompts.length} prompts actifs
          </Button>
        )}
      </CardActions>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Stack spacing={0.5}>
            {prompts?.map((p) => (
              <Stack
                key={p.promptId}
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ opacity: p.active ? 1 : 0.4 }}
              >
                <Box sx={{ pt: 0.25 }}>
                  <Flag countryCode={languageFlag(p.language)} size={15} title={p.language.toUpperCase()} />
                </Box>
                {p.zone && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ZoneFlag zone={resolveZone(p.zone)} size={13} />}
                    label={p.zone}
                    sx={{ height: 20 }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  {editingId === p.promptId ? (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <TextField
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        autoFocus
                        inputProps={{ maxLength: 500 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(p);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <Button size="small" variant="contained" disabled={savingEdit} onClick={() => saveEdit(p)}>
                        {savingEdit ? '…' : 'OK'}
                      </Button>
                      <Button size="small" disabled={savingEdit} onClick={cancelEdit}>
                        Annuler
                      </Button>
                    </Stack>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ textDecoration: p.active ? 'none' : 'line-through' }}>
                        {p.text}
                      </Typography>
                      {p.closestSku ? (
                        <Typography variant="caption" color="text.secondary">
                          SKU proche :{' '}
                          <Link
                            href={p.closestSku.url.startsWith('http') ? p.closestSku.url : `https://${p.closestSku.url}`}
                            target="_blank"
                            rel="noopener"
                          >
                            {p.closestSku.name}
                          </Link>{' '}
                          ({Math.round((p.closestSku.score || 0) * 100)}%)
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          Aucun produit candidat
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
                {editingId !== p.promptId && (
                  <>
                    <Button size="small" onClick={() => startEdit(p)}>
                      Modifier
                    </Button>
                    <Button size="small" onClick={() => toggleActive(p)}>
                      {p.active ? 'Archiver' : 'Activer'}
                    </Button>
                  </>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Card>
  );
}
